import React, { useState } from 'react';

interface FileUploadProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  className?: string;
  onFileSelect?: (file: File | null) => void;
  selectedFileProp?: File | null; // Add a prop to control the selected file from parent
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  error,
  helperText,
  className = '',
  onFileSelect,
  id,
  selectedFileProp, // <-- Destructure selectedFileProp here
  ...props
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(selectedFileProp || null);
  const [dragActive, setDragActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputId = id || `file-${Math.random().toString(36).substr(2, 9)}`;

  // Update internal state if selectedFileProp changes
  React.useEffect(() => {
    setSelectedFile(selectedFileProp || null);
  }, [selectedFileProp]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    onFileSelect?.(file);
    // No need to call props.onChange here, onFileSelect is the primary callback
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      onFileSelect?.(file);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = () => {
    setSelectedFile(null);
    onFileSelect?.(null);
    // Reset the input
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) input.value = '';
  };

  return (
    <div className={`group ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-semibold text-neutral-700 mb-3 transition-colors duration-200 group-focus-within:text-primary-600"
        >
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div
        className={`
          relative border-2 border-dashed rounded-3xl p-8 transition-all duration-300 ease-out cursor-pointer
          ${dragActive
            ? 'border-primary-500 bg-primary-50 scale-[1.02]'
            : error
            ? 'border-red-300 bg-red-50/50'
            : isHovered
            ? 'border-primary-400 bg-primary-50/50'
            : 'border-neutral-300 bg-neutral-50/50 hover:border-neutral-400 hover:bg-neutral-50'
          }
        `.replace(/\s+/g, ' ').trim()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <input
          id={inputId}
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleFileChange}
          onClick={(e) => {
            e.stopPropagation();
            // Reset the input value when clicked to allow re-selection of the same file
            const input = e.target as HTMLInputElement;
            if (input) input.value = '';
          }}
          {...props}
        />
        
        <div className="text-center">
          {selectedFile ? (
            <div className="space-y-4 animate-fade-in-up">
              {/* File Preview */}
              <div className="flex items-center justify-center">
                {selectedFile.type.startsWith('image/') ? (
                  <div className="relative">
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-2xl shadow-medium"
                    />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center shadow-medium">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-glow">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* File Info */}
              <div>
                <p className="text-lg font-semibold text-neutral-900 mb-1">{selectedFile.name}</p>
                <p className="text-sm text-neutral-500 mb-4">{formatFileSize(selectedFile.size)}</p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFile(); }}
                  className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); document.getElementById(inputId)?.click(); }}
                  className="px-4 py-2 bg-primary-100 hover:bg-primary-200 text-primary-700 rounded-xl font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Change File
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Upload Icon */}
              <div className="flex items-center justify-center">
                <div className={`
                  w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300
                  ${dragActive || isHovered
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 shadow-glow scale-110'
                    : 'bg-gradient-to-r from-neutral-400 to-neutral-500'
                  }
                `}>
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
              </div>
              
              {/* Upload Text */}
              <div>
                <p className="text-lg font-semibold text-neutral-700 mb-2">
                  <span className="text-primary-600">Click to upload</span> or drag and drop
                </p>
                <p className="text-sm text-neutral-500 mb-4">
                  {props.accept ? `Supported formats: ${props.accept}` : 'Any file type supported'}
                </p>
                
                {/* Upload Features */}
                <div className="flex items-center justify-center gap-6 text-xs text-neutral-400">
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Secure Upload
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Fast Processing
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Drag Overlay */}
        {dragActive && (
          <div className="absolute inset-0 bg-primary-500/10 border-2 border-primary-500 rounded-3xl flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-bounce-gentle">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-primary-700">Drop your file here</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mt-3 flex items-center gap-2 text-red-600 animate-fade-in-up">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}
      
      {/* Helper Text */}
      {helperText && !error && (
        <p className="mt-3 text-sm text-neutral-500 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default FileUpload;